import { FacilitatorList } from "./components/facilitatorList/FacilitatorList";
import { FacilitatorDetails } from "./model";
import {
  MuiDateFieldFormController,
  MuiGrid,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import { useRef, useState } from "react";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { AddFacilitator } from "./components/addFacilitator/AddFacilitator";
import { useFormContext, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { CoachAssignmentWrapper } from "./Facilitator.style";
import { facilitatorSiteCoreKeys } from "./SiteCoreKeys";
import {
  TableActionType,
  TableActions,
  UserDetails,
} from "../../../../../../../../helpers/types/common";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { getFullName } from "../../../../../../../../helpers/utils";
import { ProjectTabFormKey } from "../../../../../../modelsAndSchemas/Models";

interface FacilitatorProps {
  pageId: string;
}

export const Facilitator = ({ pageId }: FacilitatorProps) => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  ) as string;
  const [state, setState] = useState<{
    showDeleteConfirmModal: boolean;
  }>({
    showDeleteConfirmModal: false,
  });
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { control, setValue, getValues } = useFormContext();
  const getLabel = (key: string) =>
    fetchLabel(facilitatorSiteCoreKeys.facilitator, key);
  const selectedFacilitatorForAction = useRef<FacilitatorDetails>();
  const errorWarningMessage = getLabel(
    facilitatorSiteCoreKeys.facilitatorDeleteConfirm,
  );

  const handleActionClick = (
    action: TableActionType,
    selectedFacilitator: FacilitatorDetails,
  ) => {
    selectedFacilitatorForAction.current = selectedFacilitator;
    if (action === TableActions.delete) {
      setState((preState) => ({ ...preState, showDeleteConfirmModal: true }));
    } else if (action === TableActions.edit) {
      setState((preState) => ({ ...preState, showEditModal: true }));
    }
  };

  const facilitatorList = (useWatch({
    control,
    name: ProjectTabFormKey.projectFacilitator,
  }) ?? []) as FacilitatorDetails[];

  const setFacilitatorList = (value: FacilitatorDetails[]) => {
    setValue(ProjectTabFormKey.projectFacilitator, value, {
      shouldDirty: true,
    });
  };
  const deleteFacilitatorAssignment = () => {
    if (selectedFacilitatorForAction.current) {
      const facilitatorData = [...facilitatorList];
      const index = facilitatorData.indexOf(
        selectedFacilitatorForAction.current,
      );
      if (index !== -1) {
        facilitatorData.splice(index, 1);
        setFacilitatorList(facilitatorData);
      }
    }
  };

  const handleDeleteClick = () => {
    deleteFacilitatorAssignment();
    setState((preState) => ({ ...preState, showDeleteConfirmModal: false }));
  };

  const handleAddFacilitator = (
    selectedFunction: OptionsDataType,
    selectedFacilitator: UserDetails,
  ) => {
    const assignmentDate =
      getValues(ProjectTabFormKey.facilitatorAssignDate) ||
      dayjs().format(userCountryDateFormat);
    setFacilitatorList([
      ...facilitatorList,
      {
        assignmentDate: assignmentDate,
        facilitatorId: selectedFacilitator.id,
        facilitatorName: getFullName(
          selectedFacilitator.firstName,
          selectedFacilitator.lastName,
          true,
        ),
        functionName: selectedFunction.label,
        functionId: selectedFunction.value as string,
      },
    ]);
  };

  const handleDeleteWarningModalClose = () =>
    setState((preState) => ({ ...preState, showDeleteConfirmModal: false }));

  return (
    <CoachAssignmentWrapper>
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDateFieldFormController
            control={control}
            name={ProjectTabFormKey.facilitatorAssignDate}
            label={getLabel(facilitatorSiteCoreKeys.facilitatorAssignedDate)}
            valueFormat={userCountryDateFormat}
            format={userCountryDateFormat}
            value={
              getValues(ProjectTabFormKey.facilitatorAssignDate) !== ""
                ? dayjs(
                    getValues(ProjectTabFormKey.facilitatorAssignDate),
                    userCountryDateFormat,
                  )
                : null
            }
            id={`${pageId}-${ProjectTabFormKey.facilitatorAssignDate}`}
            disableManualInput
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>
      </MuiGrid>
      <AddFacilitator
        facilitatorList={facilitatorList}
        onAddFacilitatorCallback={handleAddFacilitator}
      />

      <FacilitatorList
        facilitatorList={facilitatorList}
        onActionClick={handleActionClick}
        dateFormat={userCountryDateFormat}
      />
      {state.showDeleteConfirmModal && (
        <WarningConfirmationModal
          show={state.showDeleteConfirmModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleDeleteWarningModalClose}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={errorWarningMessage}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </CoachAssignmentWrapper>
  );
};
